// Game state → picture. Pure functions: a panel hands over what the snapshot
// says and gets back a catalog id (or null when the moment has no picture), so
// no panel carries an `if` ladder of file names. Ids are checked against the
// catalog (`scenes.ts`) by `__tests__/sceneEvents.test.ts`.

import type { MilestonePayload } from '@aod/protocol';
import type { Relation, TreatyNewsKind } from '../dynasty/diplomacy.js';
import type { Capture, Royal } from '../dynasty/dynasty.js';
import { pickFresh } from './artPick.js';
import { SCENES, type SceneGroup, type SceneId, bossSceneId } from './scenes.js';

/** A catalog entry: which group, which id. */
export type SceneRef = {
  [G in SceneGroup]: { readonly group: G; readonly id: SceneId<G> };
}[SceneGroup];

/**
 * The picture for a royal's situation, or null while they are simply at home.
 * Held royals show the captor's decision when there is one; a royal on the road
 * is the wedding procession (the bond starts on arrival).
 *
 * With generations armed (docs/generations-design.md §6) `moments` adds the
 * two life moments: `heir` marks a generation-born child at court (the
 * birth announcement), `coronation` marks the house's new head while the
 * house mourns the old one. A dead royal shows the funeral art whichever
 * way they died — a funeral for old age too.
 */
export function royalScene(
  royal: Royal,
  capture?: Capture | null,
  moments?: { readonly heir?: boolean; readonly coronation?: boolean },
): SceneRef | null {
  switch (royal.whereabouts.kind) {
    case 'dead':
      return { group: 'royal', id: 'royal-assassinated' };
    case 'held':
      return {
        group: 'royal',
        id: capture?.choice === 'ransom' ? 'royal-ransom' : 'royal-captured',
      };
    case 'travelling':
      return { group: 'noble', id: 'royal-wedding-procession' };
    default:
      if (moments?.coronation) return { group: 'royal', id: 'royal-coronation' };
      if (moments?.heir) return { group: 'royal', id: 'royal-heir-born' };
      return royal.spouseOf !== null ? { group: 'royal', id: 'royal-wedding-feast' } : null;
  }
}

/** Most dramatic first: a death, then a captivity, a coronation, a journey, a
 *  birth, a wedding. */
const URGENCY: Readonly<Record<string, number>> = {
  'royal-assassinated': 5,
  'royal-captured': 4,
  'royal-ransom': 4,
  'royal-coronation': 3,
  'royal-wedding-procession': 2,
  'royal-heir-born': 2,
  'royal-wedding-feast': 1,
};

/**
 * The generation moments (generations §6) of one royal: `heir` — born into
 * the match (a `bornTick` of 0 or more; the dealt founding generation was
 * born before it), still a child; `coronation` — the eldest living member
 * of a house that mourns its old head. Absent while the module is dark.
 */
export function royalMoments(
  royal: Royal,
  court: readonly Royal[],
  mourningSeats: ReadonlySet<number>,
): { readonly heir?: boolean; readonly coronation?: boolean } {
  if (royal.stage === undefined) return {};
  const moments: { heir?: boolean; coronation?: boolean } = {};
  if (royal.bornTick !== undefined && royal.bornTick >= 0 && royal.stage === 'child') {
    moments.heir = true;
  }
  if (
    mourningSeats.has(royal.seat) &&
    royal.whereabouts.kind !== 'dead' &&
    court.every(
      (r) =>
        r.id === royal.id ||
        r.whereabouts.kind === 'dead' ||
        (r.bornTick ?? 0) >= (royal.bornTick ?? 0),
    )
  ) {
    moments.coronation = true;
  }
  return moments;
}

/** The one royal of a court whose situation deserves the banner, if any. */
export function courtHeadline(
  court: readonly Royal[],
  captures: readonly Capture[],
  mourningSeats?: ReadonlySet<number>,
): { readonly royal: Royal; readonly scene: SceneRef } | null {
  let best: { royal: Royal; scene: SceneRef; rank: number } | null = null;
  for (const royal of court) {
    const scene = royalScene(
      royal,
      captures.find((c) => c.royal === royal.id),
      mourningSeats ? royalMoments(royal, court, mourningSeats) : undefined,
    );
    if (!scene) continue;
    const rank = URGENCY[scene.id] ?? 0;
    if (!best || rank > best.rank) best = { royal, scene, rank };
  }
  return best ? { royal: best.royal, scene: best.scene } : null;
}

/**
 * The picture for a treaty state. `from` is the state before: a war declared out
 * of a pact or an alliance is a betrayal, and gets its own scene.
 */
export function treatyScene(state: Relation, from?: Relation | null): SceneRef | null {
  switch (state) {
    case 'war':
      return {
        group: 'diplomacy',
        id: from === 'pact' || from === 'alliance' ? 'betrayal' : 'war-declared',
      };
    case 'neutral':
      return { group: 'diplomacy', id: 'peace' };
    case 'pact':
      return { group: 'diplomacy', id: 'pact' };
    case 'alliance':
    case 'federation':
      return { group: 'diplomacy', id: 'alliance' };
    default:
      return null;
  }
}

/** Victory paths that have scenes (the catalog's `victory-<path>` ids). */
export type VictoryArtPath =
  | 'conquest'
  | 'economy'
  | 'science'
  | 'faith'
  | 'diplomacy'
  | 'milestones';

const VICTORY_POOL: Readonly<Record<VictoryArtPath, readonly SceneId<'victory'>[]>> = {
  conquest: ['victory-conquest', 'victory-conquest-2'],
  economy: ['victory-economy', 'victory-economy-2'],
  science: ['victory-science', 'victory-science-2'],
  faith: ['victory-faith', 'victory-faith-2'],
  diplomacy: ['victory-diplomacy', 'victory-diplomacy-2'],
  milestones: ['victory-milestones', 'victory-milestones-2'],
};

/**
 * The end-of-match picture. A win shows a scene of the path that won, one of two
 * per path and never the one the last win showed; a defeat shows one of three
 * defeat scenes, likewise.
 */
export function endScene(
  path: VictoryArtPath,
  won: boolean,
  rand: () => number = Math.random,
): SceneRef {
  if (!won) {
    return {
      group: 'defeat',
      id: pickFresh('end:defeat', SCENES.defeat, rand),
    };
  }
  return { group: 'victory', id: pickFresh(`end:win:${path}`, VICTORY_POOL[path], rand) };
}

/**
 * The picture for a treaty notice: what the news *is*, from the existing
 * diplomacy pool (art audit P1 — the pool was drawn for exactly this). A
 * wedding is a marriage, an acceptance wears the treaty it made, a broken
 * bond is a betrayal, a declaration is the war it starts; refusals and
 * lapses have no picture — the red line says enough.
 */
export function noticeScene(kind: TreatyNewsKind, state: Relation): SceneRef | null {
  switch (kind) {
    case 'wed':
      return { group: 'diplomacy', id: 'marriage' };
    case 'accepted':
      return state === 'pact'
        ? { group: 'diplomacy', id: 'pact' }
        : { group: 'diplomacy', id: 'alliance' };
    case 'void':
      return { group: 'diplomacy', id: 'betrayal' };
    case 'war':
      return { group: 'diplomacy', id: 'war-declared' };
    default:
      return null;
  }
}

/** The picture on an attack toast: your city under assault. */
export function attackToastScene(): SceneRef {
  return { group: 'toast', id: 'city-assaulted' };
}

/** The picture on a merchant offer: the ship at your harbour. */
export function merchantScene(): SceneRef {
  return { group: 'toast', id: 'merchant-docked' };
}

/**
 * The picture for a research breakthrough (art audit P6): one of two
 * variants, never the one the last breakthrough showed — the same
 * never-repeat idiom the victory scenes use.
 */
export function researchScene(rand: () => number = Math.random): SceneRef {
  return { group: 'research', id: pickFresh('research:breakthrough', SCENES.research, rand) };
}

/**
 * The picture for a milestone (art audit P2): one scene per **family**, so a
 * new milestone id falls back to its family's picture instead of nothing.
 * Reuses pools that already fit — peace from the diplomacy pool, the trade
 * route from the toasts, a Champion's own boss card (keyed by
 * `data.legend`). A milestone with no fitting picture (`victory.achieved`
 * has its own banner; the graduation card is the client's own) keeps its
 * icon.
 */
export function milestoneScene(m: MilestonePayload): SceneRef | null {
  switch (m.milestoneId) {
    case 'city.first_founded':
      return { group: 'milestone', id: 'city-founded' };
    case 'city.tier_up':
      return { group: 'milestone', id: 'city-tier' };
    case 'city.first_capture':
    case 'city.capital_captured':
      return { group: 'milestone', id: 'city-captured' };
    case 'hero.first_recruited':
      return { group: 'milestone', id: 'hero-recruited' };
    case 'doctrine.tier_reached':
      return { group: 'milestone', id: 'doctrine-tier' };
    case 'faith.temple_built':
      return { group: 'milestone', id: 'temple-built' };
    case 'wonder.built':
      return { group: 'milestone', id: 'wonder-built' };
    case 'magic.ritual_completed':
      return { group: 'milestone', id: 'ritual-completed' };
    case 'diplomacy.peace_signed':
      return { group: 'diplomacy', id: 'peace' };
    case 'battle.decisive':
      return { group: 'milestone', id: 'decisive-battle' };
    case 'era.advanced':
      return { group: 'milestone', id: 'era-advanced' };
    case 'sea.merchant_route':
      return { group: 'toast', id: 'trade-route-opened' };
    case 'legend.summoned':
    case 'legend.fallen': {
      const kind = typeof m.data?.legend === 'string' ? bossSceneId(m.data.legend) : null;
      return kind ? { group: 'boss', id: kind } : null;
    }
    default:
      return null;
  }
}

/**
 * The picture for a faith crisis (art audit P3): the most severe event the
 * cycle has fired. The sim publishes the fired names Rust-debug style
 * (`Schism`, `HereticUprising`, `HolyWar`); anything unknown, or a crisis
 * that has fired nothing yet, keeps the quiet chip.
 */
export function crisisScene(firedEvents: readonly string[]): SceneRef | null {
  if (firedEvents.includes('HolyWar')) return { group: 'faith', id: 'holy-war' };
  if (firedEvents.includes('HereticUprising')) return { group: 'faith', id: 'heretic-uprising' };
  if (firedEvents.includes('Schism')) return { group: 'faith', id: 'schism' };
  return null;
}

/** The picture for a doctrine artifact card: the realm's relic (P4). */
export function artifactScene(archetype: string): SceneRef | null {
  return (SCENES.artifact as readonly string[]).includes(`artifact-${archetype}`)
    ? { group: 'artifact', id: `artifact-${archetype}` as SceneId<'artifact'> }
    : null;
}
